import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Filter, Search } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useAuditEntries } from '@/api/audit';
import { relativeTime } from '@/lib/format';
import { downloadCsv, timestampedFilename } from '@/lib/download';
import { cn } from '@/lib/cn';

export function AuditRoute() {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'all' | 'success' | 'failed'>('all');
  const { data: apiEntries = [] } = useAuditEntries();

  const filtered = useMemo(() => {
    return apiEntries.filter(e => {
      if (scope !== 'all' && e.result !== scope) return false;
      if (q) {
        const hay = `${e.staff_name} ${e.action} ${e.target} ${e.tenant_name ?? ''} ${e.ip}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [q, scope, apiEntries]);

  return (
    <>
      <PageHeader
        eyebrow="Internal"
        title="Audit log"
        description="Every action taken from this console — restart, snapshot, suspend, provision, view-as-customer, incident updates. Append-only."
        actions={
          <Button
            variant="secondary"
            iconLeft={<Download size={14} />}
            onClick={() =>
              downloadCsv(
                [
                  ['timestamp', 'staff', 'action', 'target', 'tenant', 'workspace', 'result', 'ip'],
                  ...filtered.map(e => [
                    e.timestamp, e.staff_name, e.action, e.target,
                    e.tenant_name ?? '', e.workspace_id ?? '', e.result, e.ip,
                  ]),
                ],
                timestampedFilename('nousviz-audit', 'csv'),
              )
            }
          >
            Export CSV ({filtered.length})
          </Button>
        }
      />
      <PageBody>
        <Card>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <div className="relative flex-1 max-w-sm">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search staff, action, tenant, IP..."
                className="w-full h-7 pl-7 pr-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1 ml-2">
              {(['all', 'success', 'failed'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={cn(
                    'h-7 px-2.5 text-xs font-medium rounded-md border transition-colors',
                    scope === s
                      ? 'bg-surface-2 border-border-strong text-fg'
                      : 'bg-transparent border-transparent text-fg-muted hover:bg-surface-2',
                  )}
                >
                  {s === 'all' ? 'All' : s === 'success' ? 'Success' : 'Failed'}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1 text-xs text-fg-subtle">
              <Filter size={12} /> {filtered.length} of {apiEntries.length}
            </div>
          </div>

          <div className="overflow-x-auto">
          <Table className="min-w-[1000px]">
            <THead>
              <tr>
                <TH>When</TH>
                <TH>Staff</TH>
                <TH>Action</TH>
                <TH>Customer</TH>
                <TH>Reason</TH>
                <TH>IP</TH>
                <TH>Result</TH>
              </tr>
            </THead>
            <TBody>
              {filtered.map(e => (
                <TR key={e.id}>
                  <TD className="text-fg-muted">
                    <div>{relativeTime(e.timestamp)}</div>
                    <div className="text-2xs text-fg-subtle font-mono">
                      {new Date(e.timestamp).toLocaleString('en-GB', { hour12: false })}
                    </div>
                  </TD>
                  <TD>
                    <div className="font-medium text-fg">{e.staff_name}</div>
                    <div className="text-2xs text-fg-subtle">{e.staff_email}</div>
                  </TD>
                  <TD>
                    <span className="font-mono text-xs text-fg">{e.action}</span>
                    <div className="text-2xs text-fg-subtle">{e.target}</div>
                  </TD>
                  <TD>
                    {(e.tenant_slug || e.tenant_id) ? (
                      <Link to={`/tenants/${e.tenant_slug ?? e.tenant_id}`} className="text-accent hover:underline">
                        {e.tenant_name ?? e.tenant_slug}
                      </Link>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </TD>
                  <TD className="text-xs text-fg-muted max-w-xs">
                    {e.reason ?? <span className="text-fg-subtle">—</span>}
                  </TD>
                  <TD className="font-mono text-xs text-fg-muted">{e.ip}</TD>
                  <TD>
                    <Badge tone={e.result === 'success' ? 'success' : 'danger'}>{e.result}</Badge>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          </div>
          {filtered.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-fg-muted">
              {apiEntries.length === 0
                ? 'No audit entries yet. They\'ll appear as you (or system events) take actions.'
                : 'No events match your filter.'}
            </div>
          )}
        </Card>
      </PageBody>
    </>
  );
}
